import type { Action, AnyAction } from '@reduxjs/toolkit'
import { configureStore } from '@reduxjs/toolkit'
import { createLogger } from 'redux-logger'
import type { StateObservable } from 'redux-observable'
import { combineEpics, createEpicMiddleware } from 'redux-observable'
import * as Repositories from '../Repository/index.js'
import type { Observable } from 'rxjs'
import { BehaviorSubject, finalize, mergeMap, Subject, takeUntil } from 'rxjs'
import { Environment } from '../environment.js'
import { Epics } from './Epic/index.js'
import type { State } from './Slice/index.js'
import { Actions, Reducer } from './Slice/index.js'
import * as Sentry from '@sentry/react'

export type TActions = typeof Actions
export type TRepositories = typeof Repositories

export default Actions

const epic$ = new BehaviorSubject(Epics)
export const rootEpic = (action$: Observable<AnyAction>, state$: StateObservable<typeof State>, deps: TRepositories & TActions) =>
  epic$.pipe(mergeMap((epic) => epic(action$, state$, deps)))

export let dynEpics: Epic[] = []

export const epicMiddleware = createEpicMiddleware<Action, Action, typeof State, TRepositories & TActions>({
  dependencies: {
    ...Actions,
    ...Repositories
  }
})

/**
 * `diff: true` deep-compares the whole store on every action, and the store holds the OTU and
 * visualisation payloads — so this is expensive wherever it runs, besides printing the state shape
 * into the console. Gated to match `devTools` below.
 */
const developmentMiddleware = Environment.ENVIRONMENT === 'development' ? [createLogger({ collapsed: true, diff: true })] : []

const sentryReduxEnhancer = Sentry.createReduxEnhancer({})

export const Store = configureStore({
  reducer: Reducer,
  middleware: (getDefaultMiddleware) => getDefaultMiddleware().concat(developmentMiddleware).concat(epicMiddleware),
  enhancers: (getDefaultEnhancers) => getDefaultEnhancers().concat(sentryReduxEnhancer),
  devTools: Environment.ENVIRONMENT === 'development'
})

export type AppDispatch = typeof Store.dispatch
export type Epic = (
  actions$: Observable<Action>,
  state$: Observable<typeof State>,
  dependencies: TRepositories & TActions
) => Observable<Action | never>

export { useDispatch, useSelector } from './hooks.ts'

epicMiddleware.run(rootEpic)

export const addEpic = (newEpic: Epic) => {
  const notifier = new Subject<void>()
  const destructor = () => {
    notifier.next()
    notifier.unsubscribe()
  }

  epic$.next((...epicArgs) => newEpic(...epicArgs).pipe(finalize(destructor), takeUntil(notifier)))
  return destructor
}

export const removeEpic = (epic: Epic) => {
  const i = dynEpics.findIndex((e) => e.name === epic.name)
  if (i < 0) return
  dynEpics = [...dynEpics.slice(0, i - 1), ...dynEpics.slice(i + 1)]
  epicMiddleware.run(combineEpics(...dynEpics))
}
